import React, { createContext, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeContextType {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeContextProviderProps {
  children: React.ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('theme-mode') as ThemeMode;
    return savedMode || 'system';
  });

  const [isDark, setIsDark] = useState(false);

  // Funktion zum Ermitteln des aktuellen Dark Mode Status
  const getIsDark = (currentMode: ThemeMode): boolean => {
    if (currentMode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return currentMode === 'dark';
  };

  // Update isDark when mode changes or system preference changes
  useEffect(() => {
    const updateTheme = () => {
      setIsDark(getIsDark(mode));
    };

    updateTheme();

    // Listen for system theme changes only if mode is 'system'
    if (mode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => updateTheme();
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [mode]);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem('theme-mode', newMode);
  };

  const toggleTheme = () => {
    if (mode === 'system') {
      // If currently system, switch to opposite of current system preference
      const systemIsDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(systemIsDark ? 'light' : 'dark');
    } else {
      // Toggle between light and dark
      setMode(mode === 'light' ? 'dark' : 'light');
    }
  };

  const value: ThemeContextType = {
    mode,
    isDark,
    setMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
